// Ajustes > Aparência (0.67, texto 05 D3/E1; texto 07 A6/B1/B2/B3): Modo, Tema e cor de destaque, Tamanho do texto e Tamanho das caixas.
// Texto 07: tema, cor de destaque, tamanho do texto e das caixas NÃO se aplicam ao tocar. Tocar só seleciona e mostra o
// cartão de exemplo; os botões fixos embaixo ("Aplicar"/"Cancelar") confirmam. Sair sem aplicar não muda nada.
// Depois de aplicar, continua em Aparência (lib/retomar: o app remonta as telas ao trocar tema e texto).
import { useEffect, useState, type ReactNode } from 'react';
import { Pressable, Text, View } from 'react-native';
import { Btn, Chip } from '../../components/ui';
import { ABAS, C, alpha, destaqueDe, legivel, previaTema, useCores, type Modo } from '../../components/theme';
import { Listras, TemaPrevia } from '../../components/TemaPrevia';
import { ESTILOS, TEMAS, idTema, temaPorId, type EstiloTema, type Tema } from '../../lib/temas';
import { TAM_CAIXAS_PADRAO, type TamCaixas } from '../../lib/caixas';
import { escalaTexto } from '../../lib/rnTexto';
import { pedirRetomar } from '../../lib/retomar';
import { setDoc, useDoc } from '../../store/store';
import { N_, t } from '../../lib/i18n';
import { horaTela } from '../../lib/date';
import { BotaoMenu, Card } from './comum';

const ORIGINAL = N_('Original');
type TamTexto = 'p' | 'm' | 'g';
export const TAM_TEXTO: [TamTexto, string][] = [['p', N_('Pequeno')], ['m', N_('Médio')], ['g', N_('Grande')]];
export const TAM_CAIXAS: [TamCaixas, string][] = [['p', N_('Pequeno')], ['m', N_('Médio')], ['g', N_('Grande')]];
const ESCALA: Record<TamTexto, number> = { p: 0.9, m: 1, g: 1.15 };   // a mesma de lib/rn/texto.tsx
const nomeDe = (l: [string, string][], k: string) => t(l.find(x => x[0] === k)?.[1] ?? '');

type Secao = 'modo' | 'tema' | 'texto' | 'caixas';
/** Seção a reabrir depois de aplicar (o app remonta as telas; isto sobrevive à remontagem). */
let reabrir: Secao | null = null;
const aplicarERetomar = (s: Secao, mudar: () => void) => { reabrir = s; pedirRetomar(['aparencia']); mudar(); };

/** Linha compacta que abre e fecha: "Título · valor ›". */
function LinhaAbre({ icone, titulo, valor, aberto, onPress }: { icone: string; titulo: string; valor: string; aberto: boolean; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} accessibilityRole="button" accessibilityState={{ expanded: aberto }}
      style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, opacity: pressed ? 0.6 : 1 })}>
      <Text style={{ fontSize: 18 }}>{icone}</Text>
      <Text style={{ color: C.text, flex: 1, fontWeight: '600' }}>{titulo}</Text>
      <Text style={{ color: C.sub, fontSize: 13 }} numberOfLines={1}>{valor}</Text>
      <Text style={{ color: C.mute, fontSize: 18 }}>{aberto ? '⌄' : '›'}</Text>
    </Pressable>
  );
}

/** Cartão de um tema: mini prévia e nome. Tocar só seleciona (texto 07 B1). */
function CartaoTema({ id, nome, on, cor, onPress, children }: { id: string; nome: string; on: boolean; cor: string; onPress: () => void; children: ReactNode }) {
  return (
    <Pressable key={id} onPress={onPress} accessibilityRole="button" accessibilityState={{ selected: on }} accessibilityLabel={nome}
      style={({ pressed }) => ({ flexBasis: '47%', flexGrow: 1, padding: 6, gap: 6, borderRadius: 14, borderWidth: on ? 2 : 1,
        borderColor: on ? cor : C.line, backgroundColor: on ? alpha(cor, 0.1) : 'transparent', opacity: pressed ? 0.7 : 1 })}>
      {children}
      <Text style={{ color: C.text, fontWeight: on ? '800' : '600', fontSize: 13, textAlign: 'center' }} numberOfLines={1}>{on ? '✓ ' : ''}{nome}</Text>
    </Pressable>
  );
}

/** Cartão grande de exemplo de um tema num modo: fundo, cartões, botão, botão +, barra de baixo e texto. */
function ExemploTema({ tema, destaque, modo, atual, cor }: { tema: Tema | undefined; destaque: string | null; modo: Modo; atual: boolean; cor: string }) {
  const p = previaTema(tema, modo, destaque);
  return (
    <View style={{ flex: 1, gap: 4 }}>
      <Text style={{ color: atual ? C.text : C.sub, fontSize: 12, fontWeight: atual ? '800' : '600', textAlign: 'center' }}>
        {(modo === 'claro' ? '☀️ ' + t('Claro') : '🌙 ' + t('Escuro')) + (atual ? ' ✓' : '')}
      </Text>
      <View style={{ borderRadius: 14, overflow: 'hidden', backgroundColor: p.bg, borderWidth: atual ? 3 : 1, borderColor: atual ? cor : C.line, opacity: atual ? 1 : 0.85 }}>
        <View style={{ padding: 8, gap: 6 }}>
          <Text style={{ color: p.text, fontWeight: '800', fontSize: 14 }} numberOfLines={1}>My Planner</Text>
          {[0, 1].map(i => (
            <View key={i} style={{ backgroundColor: p.card, borderRadius: 10, padding: 8, gap: 3, borderWidth: 1, borderColor: p.line, flexDirection: 'row', alignItems: 'center' }}>
              <View style={{ width: 4, alignSelf: 'stretch', borderRadius: 2, backgroundColor: p.cores[i % p.cores.length], marginRight: 6 }} />
              <View style={{ flex: 1 }}>
                <Text style={{ color: p.text, fontWeight: '700', fontSize: 12 }} numberOfLines={1}>{i ? t('Estudos') : t('Reunião')}</Text>
                <Text style={{ color: p.sub, fontSize: 10 }} numberOfLines={1}>{i ? t('Descrição') : `${t('Hoje')} · ${horaTela('09:00')}`}</Text>
              </View>
            </View>
          ))}
          <View style={{ alignSelf: 'flex-start', backgroundColor: p.destaque, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 4 }}>
            <Text style={{ color: '#fff', fontWeight: '800', fontSize: 11 }}>{t('Botão')}</Text>
          </View>
        </View>
        {!!tema?.listras && <Listras cores={tema.listras} altura={3} />}
        <View style={{ backgroundColor: p.card, borderTopWidth: tema?.listras ? 0 : 1, borderTopColor: p.line, height: 30, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-around', paddingHorizontal: 6 }}>
          {p.cores.slice(0, 2).map((c, i) => <View key={i} style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: c }} />)}
          <View style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: p.destaque, alignItems: 'center', justifyContent: 'center', marginTop: -14, borderWidth: 2, borderColor: p.bg }}>
            <Text style={{ color: '#fff', fontSize: 15, fontWeight: '400', marginTop: -2 }}>+</Text>
          </View>
          {p.cores.slice(2, 4).map((c, i) => <View key={i} style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: c }} />)}
        </View>
      </View>
    </View>
  );
}

export default function Aparencia({ cor: _cor, onRodape }: { cor: string; onRodape?: (n: ReactNode) => void }) {
  const aj = useDoc('ajustes');
  const { accent } = useCores();
  const id = idTema(aj);
  const modo: Modo = aj.modo ?? 'claro';
  const [abrir, setAbrir] = useState<Secao | null>(() => { const s = reabrir; reabrir = null; return s; });

  // ─── Rascunhos: o que está selecionado, ainda sem aplicar ───
  const destaqueAtual = aj.tema?.destaque ?? null;
  const [rasc, setRasc] = useState<{ id: string; destaque: string | null }>({ id, destaque: destaqueAtual });
  const textoAtual: TamTexto = aj.tamTexto ?? 'm';
  const [texto, setTexto] = useState<TamTexto>(textoAtual);
  const caixasAtual: TamCaixas = aj.tamCaixas ?? TAM_CAIXAS_PADRAO;
  const [caixas, setCaixas] = useState<TamCaixas>(caixasAtual);
  const temaSel = temaPorId(rasc.id);
  const [estilo, setEstilo] = useState<EstiloTema>(temaSel?.estilo ?? 'profissional');

  const nomeTema = (x: Tema | undefined) => (x ? t(x.nome) : t(ORIGINAL));
  const padraoDe = (tid: string) => temaPorId(tid)?.destaque ?? destaqueDe(undefined);
  const mudouTema = rasc.id !== id || (rasc.destaque ?? padraoDe(rasc.id)) !== (destaqueAtual ?? padraoDe(id));
  const mudouTexto = texto !== textoAtual;
  const mudouCaixas = caixas !== caixasAtual;
  const cancelar = () => { setRasc({ id, destaque: destaqueAtual }); setTexto(textoAtual); setCaixas(caixasAtual); setEstilo(temaPorId(id)?.estilo ?? estilo); };
  const alternar = (q: Secao) => { cancelar(); setAbrir(a => (a === q ? null : q)); };

  const aplicarTema = () => aplicarERetomar('tema', () => setDoc('ajustes', d => ({ ...d, tema: { id: rasc.id, destaque: rasc.destaque } })));
  const aplicarTexto = () => aplicarERetomar('texto', () => setDoc('ajustes', d => ({ ...d, tamTexto: texto })));
  const aplicarCaixas = () => setDoc('ajustes', d => ({ ...d, tamCaixas: caixas }));   // não remonta: continua aqui

  // cores do tema SELECIONADO, sem repetir; tocar escolhe o destaque (só no exemplo, até aplicar)
  const cores = (temaSel ? [temaSel.destaque, ...temaSel.cores] : ABAS.slice(0, 10).map(a => a.cor)).filter((c, i, a) => a.indexOf(c) === i);
  const destaqueSel = rasc.destaque ?? padraoDe(rasc.id);

  // ─── Botões fixos embaixo (Ajustes desenha fora da rolagem) ───
  const pendente = abrir === 'tema' && mudouTema ? { l: t('Aplicar tema'), f: aplicarTema }
    : abrir === 'texto' && mudouTexto ? { l: t('Aplicar'), f: aplicarTexto }
    : abrir === 'caixas' && mudouCaixas ? { l: t('Aplicar'), f: aplicarCaixas } : null;
  useEffect(() => {
    onRodape?.(pendente ? (
      <View style={{ flexDirection: 'row', gap: 10 }}>
        <Btn outline cor={accent} label={t('Cancelar')} onPress={cancelar} style={{ flex: 1 }} />
        <Btn cor={accent} label={pendente.l} onPress={pendente.f} style={{ flex: 2 }} />
      </View>
    ) : null);
  }, [pendente?.l, rasc.id, rasc.destaque, texto, caixas, abrir, accent, id, destaqueAtual, textoAtual, caixasAtual]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onRodape?.(null), []); // eslint-disable-line react-hooks/exhaustive-deps

  const fator = (k: TamTexto) => ESCALA[k] / (escalaTexto() || 1);   // o Text do app já multiplica pela escala em uso

  return (
    <Card>
      <LinhaAbre icone={modo === 'escuro' ? '🌙' : '☀️'} titulo={t('Modo')} valor={modo === 'escuro' ? t('Escuro') : t('Claro')}
        aberto={abrir === 'modo'} onPress={() => alternar('modo')} />
      {abrir === 'modo' && (
        <View style={{ flexDirection: 'row', gap: 8, marginTop: -4 }}>
          <Chip cor={accent} on={modo === 'escuro'} label={'🌙 ' + t('Escuro')} onPress={() => modo !== 'escuro' && aplicarERetomar('modo', () => setDoc('ajustes', d => ({ ...d, modo: 'escuro' })))} />
          <Chip cor={accent} on={modo === 'claro'} label={'☀️ ' + t('Claro')} onPress={() => modo !== 'claro' && aplicarERetomar('modo', () => setDoc('ajustes', d => ({ ...d, modo: 'claro' })))} />
        </View>
      )}
      <View style={{ height: 1, backgroundColor: C.line }} />
      <LinhaAbre icone="🎨" titulo={t('Tema')} valor={nomeTema(temaPorId(id))} aberto={abrir === 'tema'} onPress={() => alternar('tema')} />
      {abrir === 'tema' && (<>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          {(['claro', 'escuro'] as Modo[]).map(m => <ExemploTema key={m} tema={temaSel} destaque={rasc.destaque} modo={m} atual={m === modo} cor={accent} />)}
        </View>
        <Text style={{ color: C.sub, fontSize: 12, textAlign: 'center', marginTop: -4 }}>{nomeTema(temaSel)}</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          <CartaoTema id="original" nome={t(ORIGINAL)} on={rasc.id === 'original'} cor={accent} onPress={() => setRasc({ id: 'original', destaque: null })}>
            <TemaPrevia tema={undefined} />
          </CartaoTema>
        </View>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          {ESTILOS.map(([e, nome]) => <Chip key={e} small cor={accent} on={estilo === e} label={t(nome)} onPress={() => setEstilo(e)} />)}
        </View>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {TEMAS.filter(x => x.estilo === estilo).map(x => (
            <CartaoTema key={x.id} id={x.id} nome={t(x.nome)} on={rasc.id === x.id} cor={accent} onPress={() => setRasc({ id: x.id, destaque: null })}>
              <TemaPrevia tema={x} />
            </CartaoTema>
          ))}
        </View>

        <Text style={{ color: C.text, fontWeight: '700', marginTop: 4 }}>{t('Cor de destaque')}</Text>
        <Text style={{ color: C.sub, fontSize: 12, marginTop: -6 }}>{t('Toque numa cor do tema {tema} para usar nos botões, no botão + e no item selecionado.', { tema: nomeTema(temaSel) })}</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {cores.map(c => {
            const on = c === destaqueSel;
            return (
              <Pressable key={c} onPress={() => setRasc(r => ({ ...r, destaque: c === padraoDe(r.id) ? null : c }))} hitSlop={4}
                accessibilityRole="button" accessibilityState={{ selected: on }} accessibilityLabel={t('Cor de destaque') + ' ' + c}
                style={{ width: 36, height: 36, borderRadius: 9, backgroundColor: legivel(c), alignItems: 'center', justifyContent: 'center',
                  borderWidth: on ? 3 : 1, borderColor: on ? C.text : C.line }}>
                {on && <Text style={{ color: '#fff', fontWeight: '900' }}>✓</Text>}
              </Pressable>
            );
          })}
        </View>
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('O tema muda as cores das abas, a cor de destaque e um toque leve no fundo, nos modos claro e escuro. Para trocar a cor de uma aba só, vá em Ajustes > Abas.')}</Text>
      </>)}

      <View style={{ height: 1, backgroundColor: C.line }} />
      <LinhaAbre icone="🔠" titulo={t('Tamanho do texto')} valor={nomeDe(TAM_TEXTO, textoAtual)} aberto={abrir === 'texto'} onPress={() => alternar('texto')} />
      {abrir === 'texto' && (<>
        <View style={{ flexDirection: 'row', gap: 8, marginTop: -4 }}>
          {TAM_TEXTO.map(([k, l]) => <Chip key={k} cor={accent} on={texto === k} label={(k === textoAtual ? '✓ ' : '') + t(l)} onPress={() => setTexto(k)} />)}
        </View>
        <View style={{ backgroundColor: C.card2, borderRadius: 14, padding: 14, gap: 6, borderWidth: 1, borderColor: C.line }}>
          <Text style={{ color: C.text, fontWeight: '800', fontSize: 17 * fator(texto) }}>{t('Exemplo')}</Text>
          <Text style={{ color: C.text, fontSize: 14 * fator(texto) }}>{t('Assim ficam as letras no app.')}</Text>
          <Text style={{ color: C.sub, fontSize: 12 * fator(texto) }}>{`${t('Hoje')} · ${horaTela('09:00')} · ${t('Reunião')}`}</Text>
        </View>
      </>)}

      <View style={{ height: 1, backgroundColor: C.line }} />
      <LinhaAbre icone="📐" titulo={t('Tamanho das caixas')} valor={nomeDe(TAM_CAIXAS, caixasAtual)} aberto={abrir === 'caixas'} onPress={() => alternar('caixas')} />
      {abrir === 'caixas' && (<>
        <View style={{ flexDirection: 'row', gap: 8, marginTop: -4 }}>
          {TAM_CAIXAS.map(([k, l]) => <Chip key={k} cor={accent} on={caixas === k} label={(k === caixasAtual ? '✓ ' : '') + t(l)} onPress={() => setCaixas(k)} />)}
        </View>
        <View style={{ backgroundColor: C.bg, borderRadius: 14, padding: 10, gap: caixas === 'p' ? 6 : caixas === 'm' ? 9 : 12, borderWidth: 1, borderColor: C.line }}>
          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700' }}>{t('Exemplo')}</Text>
          <BotaoMenu tam={caixas} icon="📅" titulo={t('Reunião')} resumo={`${t('Hoje')} · ${horaTela('09:00')}`} onPress={() => {}} />
          <BotaoMenu tam={caixas} icon="📚" titulo={t('Estudos')} resumo={t('Descrição')} onPress={() => {}} />
          <BotaoMenu tam={caixas} icon="🗒️" titulo={t('Anotações')} resumo={t('Descrição')} onPress={() => {}} />
        </View>
      </>)}
    </Card>
  );
}
